import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import hash from 'hash-sum';
import stringify from '../utils/stableStringify';
import { unpack, pack } from 'msgpackr';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import AntTime from '../utils/AntTime';
import AntString from '../utils/AntString';
import AntAuth from '../utils/AntAuth';

import PivotGrid, { FieldChooser } from 'devextreme-react/pivot-grid';

import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source';
import Chart, { AdaptiveLayout, CommonSeriesSettings, Margin, Size, Tooltip } from 'devextreme-react/chart';

import AntGridBar from './AntGridBar';
import AntQueryStore from './AntQueryStore';
import AntConfig from '../utils/AntConfig';
import AntCondition from '../utils/AntCondition';
import AntLocalization from '../utils/AntLocalization';
import AntSearchForm from './AntSearchForm';
import AntGridCategories from './AntGridCategories';
import { useGridSettingsScope } from './AntGridScope';
import {
    buildGridSettingsKey,
    readGridSettings,
    updateGridSettings,
    gridStateToSettings,
    settingsToGridState,
    takeLegacyGridEntry
} from '../utils/AntGridSettings';

import { AntGlobal, AntSession } from '../utils/AntContext';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    // const session = AntSession();
    const location = useLocation();
    const navigate = useNavigate();

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const compName = props.storageKey
        ? props.storageKey
        : props.name
        ? props.name
        : props.API && props.API.entityName
        ? props.API.entityName
        : props.entityName
        ? props.entityName
        : props.url
        ? props.url
        : undefined;

    // Cấu hình trong object chung AntGridSettings: khoá <phạm vi>/pivot:<tên> (cùng khuôn sort/filters/view với AntGrid).
    const { scope: pivotSettingsScope, route: pivotSettingsRoute } = useGridSettingsScope();
    const pivotSettingsName = String(compName || '').replace(/[#/]/g, '.');
    const pivotSettingsKey = buildGridSettingsKey(pivotSettingsScope, 'pivot:' + pivotSettingsName);
    const pivotSettingsMeta = { scope: pivotSettingsScope, route: pivotSettingsRoute, grid: 'pivot:' + pivotSettingsName };
    const pivotSettingsMigrated = useRef(false);
    if (!pivotSettingsMigrated.current) {
        pivotSettingsMigrated.current = true;
        const oldState = takeLegacyGridEntry('pivot-grid-states', hash(String(location.pathname + '-' + compName)));
        if (oldState && typeof oldState === 'object') {
            const sections = gridStateToSettings(oldState);
            updateGridSettings(
                pivotSettingsKey,
                (entry) => {
                    const next = { ...entry };
                    Object.keys(sections).forEach((k) => {
                        if (next[k] === undefined && sections[k]) next[k] = sections[k];
                    });
                    return next;
                },
                pivotSettingsMeta
            );
        }
    }

    const getCachingStates = useCallback(() => {
        const defaultObj = { searchText: '', sortByColumn: null, sortType: null, filteredGroup: null };
        return { ...defaultObj, ...settingsToGridState(readGridSettings(pivotSettingsKey)) };
    });

    const saveCachingStates = () => {
        {
            let obj = {
                sortByColumn: sortByColumn.current ? String(sortByColumn.current) : null,
                sortType: sortType.current ? String(sortType.current) : null,
                searchText: searchText.current !== null && searchText.current !== undefined ? String(searchText.current) : '',
                filteredGroup: filteredGroup.current ? stringify(filteredGroup.current) : null,
                selectedCats1: selectedCats1.current,
                selectedCats2: selectedCats2.current,
                selectedCats3: selectedCats3.current,
                selectedCats4: selectedCats4.current,
                selectedCats5: selectedCats5.current,
                selectedCats6: selectedCats6.current,
                selectedCats7: selectedCats7.current,
                selectedCats8: selectedCats8.current,
                selectedCats9: selectedCats9.current,
                selectedCats10: selectedCats10.current,
                showSearchForm: showSearchForm,
                showChart: showChart
            };

            if (obj.searchText === '' || obj.searchText === 'undefined') delete obj.searchText;
            if (!obj.sortByColumn) delete obj.sortByColumn;
            if (!obj.sortType) delete obj.sortType;
            if (!obj.filteredGroup) delete obj.filteredGroup;

            const sections = gridStateToSettings(obj);
            updateGridSettings(pivotSettingsKey, (entry) => ({ ...entry, ...sections }), pivotSettingsMeta);
        }
    };

    const defaultStates = useMemo(() => getCachingStates(), []);

    const itemsCats1 = useRef([]);
    const itemsCats2 = useRef([]);
    const itemsCats3 = useRef([]);
    const itemsCats4 = useRef([]);
    const itemsCats5 = useRef([]);
    const itemsCats6 = useRef([]);
    const itemsCats7 = useRef([]);
    const itemsCats8 = useRef([]);
    const itemsCats9 = useRef([]);
    const itemsCats10 = useRef([]);

    const itemsCats1Loaded = useRef(false);
    const itemsCats2Loaded = useRef(false);
    const itemsCats3Loaded = useRef(false);
    const itemsCats4Loaded = useRef(false);
    const itemsCats5Loaded = useRef(false);
    const itemsCats6Loaded = useRef(false);
    const itemsCats7Loaded = useRef(false);
    const itemsCats8Loaded = useRef(false);
    const itemsCats9Loaded = useRef(false);
    const itemsCats10Loaded = useRef(false);

    const selectedCats1 = useRef(defaultStates && defaultStates.selectedCats1 !== undefined ? defaultStates.selectedCats1 : []);
    const selectedCats2 = useRef(defaultStates && defaultStates.selectedCats2 !== undefined ? defaultStates.selectedCats2 : []);
    const selectedCats3 = useRef(defaultStates && defaultStates.selectedCats3 !== undefined ? defaultStates.selectedCats3 : []);
    const selectedCats4 = useRef(defaultStates && defaultStates.selectedCats4 !== undefined ? defaultStates.selectedCats4 : []);
    const selectedCats5 = useRef(defaultStates && defaultStates.selectedCats5 !== undefined ? defaultStates.selectedCats5 : []);
    const selectedCats6 = useRef(defaultStates && defaultStates.selectedCats6 !== undefined ? defaultStates.selectedCats6 : []);
    const selectedCats7 = useRef(defaultStates && defaultStates.selectedCats7 !== undefined ? defaultStates.selectedCats7 : []);
    const selectedCats8 = useRef(defaultStates && defaultStates.selectedCats8 !== undefined ? defaultStates.selectedCats8 : []);
    const selectedCats9 = useRef(defaultStates && defaultStates.selectedCats9 !== undefined ? defaultStates.selectedCats9 : []);
    const selectedCats10 = useRef(defaultStates && defaultStates.selectedCats10 !== undefined ? defaultStates.selectedCats10 : []);

    const initDefaultFilteredGroup = () => {
        if (mainActions instanceof Array) {
            var item = mainActions.find((i) => i.widget === 'ButtonGroup');
            if (item && item.items instanceof Array && item.items.length > 0) {
                if (props.filteredGroup) {
                    var found = item.items.find((i) => i.name === props.filteredGroup);
                    if (found) return found;
                }
                if (item.defaultSelect) {
                    var found = item.items.find((i) => i.name === item.defaultSelect);
                    if (found) return found;
                }
                if (item && item.items instanceof Array) {
                    var found = item.items.find((i) => i.default === true);
                    if (found) return [found];
                }
                if (item.items.length > 0) {
                    return item.items[0];
                }
            }
        }

        return null;
    };

    const beginUpdate = () => {
        if (grid.current && grid.current.instance) {
            grid.current.instance.beginUpdate();
        }
    };

    const endUpdate = () => {
        if (grid.current && grid.current.instance) {
            grid.current.instance.updateDimensions();
            grid.current.instance.endUpdate();
        }
    };

    const initDataSource = () => {
        return new AntQueryStore({
            key: props.keyExpr,
            API: props.API,
            url: props.url,
            loadFunc: props.loadFunc,
            // remotePaging: false,

            getSearchConditions: dsGetSearchConditions,
            getParentSearchConditions: dsGetParentSearchConditions,
            getFilteredGroupConditions: dsGetFilteredGroupConditions,
            getQueryFields: dsGetQueryFields,
            getSearchText: dsGetSearchText,
            getOrderBy: dsGetOrderBy,
            getOrderings: dsGetOrderings,
            getOrderType: dsGetOrderType,
            isAllowQuery: isAllowQuery,
            onLoading: onDatasourceLoading,
            onLoaded: onDatasourceLoaded
        });
    };

    const initPivotDataSource = (ds) => {
        if (props.API && !props.API.customData) {
            if (
                props &&
                props.configFields instanceof Array &&
                props.configFields.some(
                    (f) =>
                        f.dataField === 'monthAndYear' ||
                        f.dataField === 'monthInYear' ||
                        f.dataField === 'dayInYear' ||
                        f.dataField === 'monthAndYearAndDay'
                )
            ) {
                var fieldName =
                    props.queryFields instanceof Array && props.queryFields.length === 1
                        ? props.queryFields[0]
                        : props.selectFields instanceof Array && props.selectFields.length === 1
                        ? props.selectFields[0]
                        : 'created_at';

                // Báo cáo phải gom/hiển thị theo giờ VẬN HÀNH của business, không phải giờ trình
                // duyệt của người đang xem — 2 người xem cùng lúc từ 2 múi giờ khác nhau phải luôn
                // ra CÙNG một nhãn tháng/ngày. business_timezone lấy từ session đăng nhập (BE trả
                // về ở ACUserAuthen); rơi về giờ trình duyệt nếu vì lý do gì đó chưa có (session cũ
                // chưa đăng nhập lại từ khi field này được thêm).
                var reportTimezone = AntAuth.getSessionInfo('business_timezone');

                props.API.customData = (data) => {
                    if (data[fieldName]) {
                        var date = AntTime.fromUnix(data[fieldName], reportTimezone).toDate();
                        var year = AntTime.fromUnix(data[fieldName], reportTimezone).format('YYYY');
                        var month = AntTime.fromUnix(data[fieldName], reportTimezone).format('MM');
                        var day = AntTime.fromUnix(data[fieldName], reportTimezone).format('DD');

                        data.colDate = date;
                        data.year = AntString.format('{0}', year);
                        data.monthAndYear = AntString.format('{0}/{1}', month, year);
                        data.monthInYear = AntString.format('{0}/{1}', month, year);
                        data.monthAndYearAndDay = AntString.format('{0}/{1}/{2}', day, month, year);
                        data.dayInYear = AntString.format('{0}/{1}/{2}', day, month, year);
                    }
                    return data;
                };
            }
        }

        var ar = props.configFields.map((f) => {
            if (f.format === 'currency') {
                f.format = (value) => {
                    return AntLocalization.translateCurrency(value, f.showSign);
                };
            }

            if (f.format === 'int') {
                f.format = (value) => {
                    return AntLocalization.translateInteger(value);
                };
            }

            if (f.format === 'decimal') {
                f.format = (value) => {
                    return AntLocalization.translateDecimal(value);
                };
            }

            if (f.dataField === 'monthAndYear' || f.dataField === 'monthInYear') {
                f.sortingMethod = function (a, b) {
                    try {
                        if (String(a.value).includes('/') && String(b.value).includes('/')) {
                            var arA = a.value.split('/');
                            var arB = b.value.split('/');
                            var aValue = parseInt(arA[1]) * 100 + parseInt(arA[0]);
                            var bValue = parseInt(arB[1]) * 100 + parseInt(arB[0]);
                            return aValue - bValue;
                        }
                    } catch (error) {
                        console.log('ant-monthInYear-error) : ', error);
                        console.log('ant-monthInYear-a : ', a);
                        console.log('ant-monthInYear-b : ', b);
                    }
                    return -1;
                };
            } else if (f.dataField === 'monthAndYearAndDay' || f.dataField === 'dayInYear') {
                f.sortingMethod = function (a, b) {
                    try {
                        if (String(a.value).includes('/') && String(b.value).includes('/')) {
                            var arA = a.value.split('/');
                            var arB = b.value.split('/');
                            var aValue = parseInt(arA[2]) * 100 * 100 + parseInt(arA[1]) * 100 + parseInt(arA[0]);
                            var bValue = parseInt(arB[2]) * 100 * 100 + parseInt(arB[1]) * 100 + parseInt(arB[0]);

                            if (f.sortOrder === 'asc') return bValue - aValue;

                            return aValue - bValue;
                        }
                    } catch (error) {
                        console.log('ant-dayInYear-error) : ', error);
                        console.log('ant-dayInYear-a : ', a);
                        console.log('ant-dayInYear-b : ', b);
                    }
                    return -1;
                };
            }
            return f;
        });

        return new PivotGridDataSource({
            store: ds.instance(),
            fields: ar
        });
    };

    const dsGetSearchText = () => {
        return searchText.current;
    };

    const dsGetQueryFields = useCallback((opts) => {
        var ar = ['created_at', 'checksum', 'id', 'code'];

        if (props.configFields instanceof Array) {
            for (let i = 0; i < props.configFields.length; i++) {
                const c = props.configFields[i];

                var fieldName = null;
                if (typeof c === 'object' && c.dataField) fieldName = c.dataField;

                if (fieldName) ar.push(fieldName);
            }
        }

        if (props.showDetail) {
            if (
                typeof props.showDetail === 'object' &&
                props.showDetail.key &&
                !ar.includes(props.showDetail.key) &&
                !ar.some((a) => a && typeof a === 'object' && a.field === props.showDetail.key)
            ) {
                ar.push(props.showDetail.key);
            } else if (props.showDetail instanceof Array) {
                for (let i = 0; i < props.showDetail.length; i++) {
                    const item = props.showDetail[i];

                    if (
                        typeof item === 'object' &&
                        item.key &&
                        !ar.includes(item.key) &&
                        !ar.some((a) => a && typeof a === 'object' && a.field === item.key)
                    ) {
                        ar.push(item.key);
                    }
                }
            }
        }

        if (props.queryFields instanceof Array) ar.push(...props.queryFields);
        if (props.selectFields instanceof Array) ar.push(...props.selectFields);

        if (props.getQueryFields) {
            var fields = props.getQueryFields();
            if (fields instanceof Array) ar.push(...fields);
        }

        ar = ar
            .filter((v) => Boolean(v))
            // .filter((v) => Boolean(v.alias))
            .filter((v, index, self) => {
                return self.indexOf(v) === index;
            });

        return ar;
    });

    const dsGetSearchConditions = () => {
        return searchConditions.current ? JSON.parse(stringify(searchConditions.current)) : {};
    };

    const dsGetParentSearchConditions = () => {
        return searchParentConditions ? JSON.parse(stringify(searchParentConditions)) : {};
    };

    const dsGetFilteredGroupConditions = () => {
        if (filteredGroup.current && filteredGroup.current.searchConditions) return filteredGroup.current.searchConditions;

        return {};
    };

    const dsGetOrderBy = () => {
        if (sortByColumn.current) return sortByColumn.current;
    };

    const dsGetOrderings = useCallback(() => {
        if (props.ordering instanceof Array) return props.ordering;
        return null;
    });

    const dsGetOrderType = () => {
        return sortType.current;
    };

    const _baseInitMenuItem = useCallback((obj) => {
        if (!obj) return null;

        var current = {
            valueExpr: obj.valueExpr,
            displayExpr: obj.displayExpr,
            icon: obj.icon,
            text: obj.label ? AntLocalization.translateText(obj.label) : AntLocalization.translateText(obj.text),

            value: obj.value,
            width: obj.width,
            stylingMode: obj.stylingMode ? obj.stylingMode : 'text'
        };

        if (obj.items instanceof Array && obj.items.length > 0) {
            current.items = [];

            obj.items.forEach((i) => {
                var child = _baseInitMenuItem(i);
                if (child) current.items.push(child);
            });
        }
        return current;
    });

    const baseInitMenuItem = useCallback((mItem) => {
        if (!mItem || !mItem.isMenu) return;

        var m = _baseInitMenuItem(mItem);
        if (m) {
            return {
                ...mItem,
                widget: 'Menu',
                location: mItem.location,
                items: [m]
            };
        }
    });

    const initActions = () => {
        var actions = props.actionItems instanceof Array ? props.actionItems : [];
        if (actions instanceof Array)
            actions = actions.map((c) => {
                var nItem = JSON.parse(stringify(c));

                nItem.text = AntLocalization.translateText(nItem.text);
                if (nItem.isMenu) {
                    nItem = baseInitMenuItem(nItem);
                } else if (nItem.widget === 'ButtonGroup') {
                    if (nItem.items instanceof Array) {
                        nItem.items = nItem.items.filter((item) => {
                            return AntCondition.isVisible(props.parentData, item);
                        });
                    }

                    if (nItem.defaultSelect && (typeof nItem.defaultSelect === 'object' || nItem.defaultSelect instanceof Array))
                        nItem.defaultSelect = AntCondition.getValue(props.parentData, nItem.defaultSelect);
                }

                return nItem;
            });

        actions = actions.filter((item) => {
            var isVisible = AntCondition.isVisible(props.parentData, item);
            if (
                isVisible &&
                typeof item.visible === 'object' &&
                item.visible &&
                typeof item.visible.whenAction === 'object' &&
                item.visible.whenAction
            ) {
                isVisible = AntCondition.isVisible(searchConditions.current, {
                    visible: {
                        when: {
                            ...item.visible.whenAction
                        }
                    }
                });
            }
            return isVisible;
        });
        actions.forEach((item) => {
            item.editable = AntCondition.isEditable(props.parentData, item);
        });
        return actions;
    };

    const generateSearchConditions = (opts) => {
        let objSearch = {};

        if (props.searchConditions) {
            if (typeof props.searchConditions === 'function') {
                var obj = props.searchConditions();
                if (obj)
                    objSearch = {
                        ...objSearch,
                        ...obj
                    };
            } else {
                objSearch = {
                    ...objSearch,
                    ...props.searchConditions
                };
            }
        }

        objSearch = {
            ...formSearchConditions,
            ...objSearch
        };

        if (props.actionItems instanceof Array) {
            props.actionItems.forEach((item) => {
                if (item.name && item.widget !== 'Button' && item.widget !== 'ButtonGroup' && item.widget !== 'Menu')
                    objSearch[item.name] = item.defaultValue !== undefined ? item.defaultValue : false;
            });
        }

        if (actionBar.current) {
            var obj = actionBar.current.getSearchConditions();
            if (obj)
                objSearch = {
                    ...objSearch,
                    ...obj
                };
        }

        if (props.searchFields instanceof Array && props.searchFields.length > 0) {
            Object.keys(objSearch).map((k) => {
                var found = props.searchFields.find((f) => typeof f === 'object' && f.alias && f.field === k);
                if (found) {
                    objSearch[found.alias] = objSearch[k];
                    delete objSearch[k];
                }
            });

            Object.keys(objSearch).map((k) => {
                var found = props.searchFields.find(
                    (f) =>
                        f === k ||
                        f === k + '_oneMonth' ||
                        f === k + '_oneYear' ||
                        f === k + '_startDate' ||
                        f === k + '_startMonth' ||
                        f === k + '_startYear' ||
                        f === k + '_endDate' ||
                        f === k + '_endMonth' ||
                        f === k + '_endYear' ||
                        k === f + '_oneMonth' ||
                        k === f + '_oneYear' ||
                        k === f + '_startDate' ||
                        k === f + '_startMonth' ||
                        k === f + '_startYear' ||
                        k === f + '_endDate' ||
                        k === f + '_endMonth' ||
                        k === f + '_endYear' ||
                        (typeof f === 'object' && f.alias === k)
                );

                if (!found && props.actionItems instanceof Array) found = props.actionItems.find((f) => f.name === k);

                if (!found) objSearch[k] = undefined;
            });
        }

        if (props.searchCategories1 && props.searchCategories1.lookups instanceof Array)
            props.searchCategories1.lookups.forEach((k) => delete objSearch[k.field]);
        if (props.searchCategories2 && props.searchCategories2.lookups instanceof Array)
            props.searchCategories2.lookups.forEach((k) => delete objSearch[k.field]);
        if (props.searchCategories3 && props.searchCategories3.lookups instanceof Array)
            props.searchCategories3.lookups.forEach((k) => delete objSearch[k.field]);
        if (props.searchCategories4 && props.searchCategories4.lookups instanceof Array)
            props.searchCategories4.lookups.forEach((k) => delete objSearch[k.field]);
        if (props.searchCategories5 && props.searchCategories5.lookups instanceof Array)
            props.searchCategories5.lookups.forEach((k) => delete objSearch[k.field]);
        if (props.searchCategories6 && props.searchCategories6.lookups instanceof Array)
            props.searchCategories6.lookups.forEach((k) => delete objSearch[k.field]);
        if (props.searchCategories7 && props.searchCategories7.lookups instanceof Array)
            props.searchCategories7.lookups.forEach((k) => delete objSearch[k.field]);
        if (props.searchCategories8 && props.searchCategories8.lookups instanceof Array)
            props.searchCategories8.lookups.forEach((k) => delete objSearch[k.field]);
        if (props.searchCategories9 && props.searchCategories9.lookups instanceof Array)
            props.searchCategories9.lookups.forEach((k) => delete objSearch[k.field]);
        if (props.searchCategories10 && props.searchCategories10.lookups instanceof Array)
            props.searchCategories10.lookups.forEach((k) => delete objSearch[k.field]);

        if (props.searchCategories1)
            for (let i = 0; i < selectedCats1.current.length; i++) {
                const c = selectedCats1.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }
        if (props.searchCategories2)
            for (let i = 0; i < selectedCats2.current.length; i++) {
                const c = selectedCats2.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }

        if (props.searchCategories3)
            for (let i = 0; i < selectedCats3.current.length; i++) {
                const c = selectedCats3.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }

        if (props.searchCategories4)
            for (let i = 0; i < selectedCats4.current.length; i++) {
                const c = selectedCats4.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }

        if (props.searchCategories5)
            for (let i = 0; i < selectedCats5.current.length; i++) {
                const c = selectedCats5.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }

        if (props.searchCategories6)
            for (let i = 0; i < selectedCats6.current.length; i++) {
                const c = selectedCats6.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }

        if (props.searchCategories7)
            for (let i = 0; i < selectedCats7.current.length; i++) {
                const c = selectedCats7.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }

        if (props.searchCategories8)
            for (let i = 0; i < selectedCats8.current.length; i++) {
                const c = selectedCats8.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }

        if (props.searchCategories9)
            for (let i = 0; i < selectedCats9.current.length; i++) {
                const c = selectedCats9.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }

        if (props.searchCategories10)
            for (let i = 0; i < selectedCats10.current.length; i++) {
                const c = selectedCats10.current[i];
                if (c.value instanceof Array) {
                    if (c.value.length > 0) objSearch[c.field] = c.value;
                } else if (c.value) objSearch[c.field] = c.value;
            }

        Object.keys(objSearch).map((k) => {
            if (objSearch[k] !== null && typeof objSearch[k] === 'object') objSearch[k] = AntCondition.getValueFromSession(objSearch[k]);
        });

        Object.keys(objSearch).map((k) => {
            if (objSearch[k] === null || objSearch[k] === '') objSearch[k] = undefined;
        });

        Object.keys(objSearch).forEach((key) => {
            if (objSearch[key] === undefined) {
                delete objSearch[key];
            }
        });
        Object.keys(objSearch).forEach((key) => {
            if (objSearch[key] instanceof Array) {
                objSearch[key + '_search_inner_join'] = true;
            }
        });

        if (props.isSearching) objSearch.isSearching = true;

        searchConditions.current = objSearch;

        if (opts && opts.autoReload) autoReload();

        return objSearch;
    };

    const hasFilterGroups = useCallback(() => {
        if (props.actionItems instanceof Array) {
            var found = props.actionItems.find((i) => i.widget === 'ButtonGroup');
            if (found) return true;
        }

        return false;
    });

    const isAllowQuery = () => {
        if (props.searchConditionsRequired && (!searchConditions.current || Object.keys(searchConditions.current).length === 0)) return false;

        if (props.isAllowQuery && typeof props.isAllowQuery === 'function') return props.isAllowQuery();

        return true;
    };

    const getKeyField = useCallback(() => {
        return props.keyExpr ? props.keyExpr : 'id';
    });

    const getCachedData = () => {
        if (dataSource.current) return dataSource.current.getCachedData();
        return null;
    };

    const reload = async (opts) => {
        refresh(true);
    };

    const cleanData = useCallback(() => {
        dataSource.current.reset(null, true, true);
    });

    const refresh = () => {
        // generateSearchConditions();
        if (pivotGridDataSource.current) {
            pivotGridDataSource.current.reload();
        }
    };

    const repaint = () => {
        if (grid.current && grid.current.instance) {
            grid.current.instance.repaint();
        }
    };

    const refreshAndPaint = async () => {
        if (grid.current && grid.current.instance) {
            await dataSource.current.willGetLastResult();
            if (pivotGridDataSource.current) {
                pivotGridDataSource.current.reload();
            }
        }
    };

    const isDataLoading = () => {
        return Boolean(loading);
    };

    const isDataLoaded = () => {
        return Boolean(loaded.current);
    };

    const onDatasourceLoading = () => {
        loading.current = true;
    };

    const onDatasourceLoaded = (dataRows) => {
        loading.current = false;
        loaded.current = true;

        autoResetUI();

        if (actionBar.current) actionBar.current.runCounting();

        if (props.onLoaded) props.onLoaded(dataRows);

        var sTextValue = dataRows && dataRows.conditions instanceof Array ? dataRows.conditions.find((c) => c.name === 'searchText') : null;
        sTextValue = sTextValue ? sTextValue.value : null;

        var totalCount = dataRows ? dataRows.totalCount : null;
        var rows = dataRows && dataRows.data instanceof Array ? dataRows.data : [];
        var row = rows instanceof Array && rows.length === 1 && totalCount === 1 ? rows[0] : null;

        if (actionBar.current) {
            actionBar.current.resetItems({
                shouldCount: true
            });
        }

        // if (rows.length <= 0) {
        //     refresh();
        //     setTimeout(() => {
        //         refresh();
        //     }, 500);
        // }
    };

    const resetSelectedActionGroups = (selectedGroup) => {
        if (actionBar.current) {
            actionBar.current.resetSelectGroups(selectedGroup);
        }
    };

    const onSearchText = (text) => {
        searchText.current = text;
        refresh();
        if (props.onSearchText) props.onSearchText(text);
    };

    const onStartSearchText = () => {
        setSearchBarFocused(true);
    };

    const onActionDownload = () => {
        if (dataSource.current) dataSource.current.callDownloadAPI();
    };

    const onAction = (e) => {
        if (e.name) generateSearchConditions({ autoReload: true });

        if (props.onAction && typeof props.onAction === 'function') props.onAction(e);
    };
    const resetVisibility = (visible) => {
        if (grid.current && grid.current.instance) {
            var oldVisible = grid.current.instance.option('visible');
            if (oldVisible != visible) grid.current.instance.option('visible', visible);
        }
        if (props.showActionBar != false && actionBar.current) actionBar.current.resetVisibility(visible);
    };

    const resetDisabled = (disabled) => {
        if (grid.current && grid.current.instance) {
            var oldDisabled = grid.current.instance.option('disabled');
            if (oldDisabled != disabled) grid.current.instance.option('disabled', disabled);
        }
        if (props.showActionBar != false && actionBar.current) actionBar.current.resetDisabled(disabled);
    };

    const onOptionChanged = (e) => {
        if (props.onOptionChanged) props.onOptionChanged(e);
    };

    const getSearchCategories = () => {
        let ar = [];
        if (
            props.searchCategories1 &&
            props.searchCategories1.lookups instanceof Array &&
            props.searchCategories1.lookups.length > 0 &&
            itemsCats1.current.length > 0
        )
            ar.push({
                categories: props.searchCategories1.lookups,
                items: itemsCats1.current,
                selectedItems: selectedCats1.current
            });

        if (
            props.searchCategories2 &&
            props.searchCategories2.lookups instanceof Array &&
            props.searchCategories2.lookups.length > 0 &&
            itemsCats2.current.length > 0
        )
            ar.push({
                categories: props.searchCategories2.lookups,
                items: itemsCats2.current,
                selectedItems: selectedCats2.current
            });

        if (
            props.searchCategories3 &&
            props.searchCategories3.lookups instanceof Array &&
            props.searchCategories3.lookups.length > 0 &&
            itemsCats3.current.length > 0
        )
            ar.push({
                categories: props.searchCategories3.lookups,
                items: itemsCats3.current,
                selectedItems: selectedCats3.current
            });

        if (
            props.searchCategories4 &&
            props.searchCategories4.lookups instanceof Array &&
            props.searchCategories4.lookups.length > 0 &&
            itemsCats4.current.length > 0
        )
            ar.push({
                categories: props.searchCategories4.lookups,
                items: itemsCats4.current,
                selectedItems: selectedCats4.current
            });

        if (
            props.searchCategories5 &&
            props.searchCategories5.lookups instanceof Array &&
            props.searchCategories5.lookups.length > 0 &&
            itemsCats5.current.length > 0
        )
            ar.push({
                categories: props.searchCategories5.lookups,
                items: itemsCats5.current,
                selectedItems: selectedCats5.current
            });

        if (
            props.searchCategories6 &&
            props.searchCategories6.lookups instanceof Array &&
            props.searchCategories6.lookups.length > 0 &&
            itemsCats6.current.length > 0
        )
            ar.push({
                categories: props.searchCategories6.lookups,
                items: itemsCats6.current,
                selectedItems: selectedCats6.current
            });

        if (
            props.searchCategories7 &&
            props.searchCategories7.lookups instanceof Array &&
            props.searchCategories7.lookups.length > 0 &&
            itemsCats7.current.length > 0
        )
            ar.push({
                categories: props.searchCategories7.lookups,
                items: itemsCats7.current,
                selectedItems: selectedCats7.current
            });

        if (
            props.searchCategories8 &&
            props.searchCategories8.lookups instanceof Array &&
            props.searchCategories8.lookups.length > 0 &&
            itemsCats8.current.length > 0
        )
            ar.push({
                categories: props.searchCategories8.lookups,
                items: itemsCats8.current,
                selectedItems: selectedCats8.current
            });

        if (
            props.searchCategories9 &&
            props.searchCategories9.lookups instanceof Array &&
            props.searchCategories9.lookups.length > 0 &&
            itemsCats9.current.length > 0
        )
            ar.push({
                categories: props.searchCategories9.lookups,
                items: itemsCats9.current,
                selectedItems: selectedCats9.current
            });

        if (
            props.searchCategories10 &&
            props.searchCategories10.lookups instanceof Array &&
            props.searchCategories10.lookups.length > 0 &&
            itemsCats10.current.length > 0
        )
            ar.push({
                categories: props.searchCategories10.lookups,
                items: itemsCats10.current,
                selectedItems: selectedCats10.current
            });

        return ar;
    };

    const resetUI = () => {
        // beginUpdate();
        // endUpdate();
    };

    const autoResetUI = () => {
        resetUI();
        if (autoResetUITimer.current) clearInterval(autoResetUITimer.current);
        autoResetUITimer.current = setInterval(() => {
            resetUI();
        }, 5 * 1000);

        saveCachingStates();
    };

    const autoReload = () => {
        reload();
        if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
        autoRefreshTimer.current = setInterval(() => {
            reload();
        }, 30 * 1000);
    };

    // const forceReload = () => {
    //     generateSearchConditions({ autoReload: true });

    //     autoReload();

    //     setTimeout(() => {
    //         resetUI();
    //     }, 500);
    // };

    const bindChart = (cmpChart, opts) => {
        if (grid.current && grid.current.instance) grid.current.instance.bindChart(cmpChart, opts);
    };

    const autoBindChart = () => {
        if (grid.current && grid.current.instance && chart.current && chart.current.instance) {
            bindChart(chart.current.instance, {
                dataFieldsDisplayMode: 'splitPanes',
                alternateDataFields: false,
                processCell: (e) => {
                    let strEnd = AntLocalization.translateText({ vi: 'Tổng tất cả', en: 'Total grand', fr: 'Total grand', es: 'Total grand', id: 'Total grand', ja: 'Total grand', zh: 'Total grand', ko: 'Total grand', hi: 'Total grand', ar: 'Total grand' });

                    e.visible = [strEnd].includes(e.chartDataItem.arg) === false;

                    if (e.visible && props.chart.showGrandTotals)
                        e.visible =
                            e.rowPathFormatted.findIndex((c) => [strEnd].includes(c)) >= 0 &&
                            e.columnPathFormatted.findIndex((c) => [strEnd].includes(c)) < 0;

                    if (e.visible && props.chart.processCell) props.chart.processCell(e);
                },

                customizeChart: (chartOptions) => {
                    chartOptions.commonSeriesSettings = {
                        type: 'bar'
                    };
                    if (chartOptions.valueAxis[0]) chartOptions.valueAxis[0].title = undefined;
                    chartOptions.legend = {
                        orientation: 'horizontal',
                        verticalAlignment: 'bottom',
                        horizontalAlignment: 'center'
                    };
                    if (props.isCurrency)
                        chartOptions.tooltip = {
                            format: (value) => {
                                return AntLocalization.translateCurrency(value);
                            },
                            enabled: true
                        };
                    if (props.isMonthlyReport) {
                        chartOptions.argumentAxis = {
                            label: {
                                // customizeText: (e) => {
                                //     var strs = String(e.value).split('/');
                                //     if (strs.length === 2) {
                                //         var year = strs[0];
                                //         var quarter = strs[1].replace('Q', '');
                                //         e.valueText = 'Quý ' + quarter + '/' + year;
                                //     } else if (strs.length === 3) {
                                //         var year = strs[0];
                                //         var quarter = strs[1];
                                //         var month = strs[2].split(' ')[1];
                                //         e.valueText = month + '/' + year;
                                //     }
                                //     return e.valueText;
                                // }

                                customizeText: (e) => {
                                    var pivotConfigFields = props && props.configFields instanceof Array ? props.configFields : [];

                                    if (pivotConfigFields.some((f) => f.dataField === 'dayInYear')) {
                                        if (String(e.valueText).includes('/')) {
                                            try {
                                                var ar = String(e.valueText).split('/');
                                                ar.pop();
                                                return ar.join('/');
                                            } catch (error) {
                                                console.log('catch-valueText- : ', e.valueText, e);
                                            }
                                        }
                                    }

                                    return AntLocalization.translateText(e.valueText);
                                }
                            }
                        };
                    }
                    if (props.chart.options) chartOptions = Object.assign(chartOptions, props.chart.options);
                    if (props.chart.customizeChart) chartOptions = props.chart.customizeChart(chartOptions);

                    return chartOptions;
                },

                customizeSeries: (seriesName, seriesOptions) => {
                    // Change series properties here

                    if (props.chart.customizeSeries) seriesOptions = props.chart.customizeSeries(seriesName, seriesOptions);

                    return seriesOptions; // This line is optional
                }
            });

            setTimeout(() => {
                if (chart.current.instance) chart.current.instance.refresh();
            });
        }
    };

    const showRefresh = Boolean(props.showRefresh);

    const sortByColumn = useRef(defaultStates.sortByColumn);
    const sortType = useRef(defaultStates.sortType);

    const loading = useRef(false);
    const loaded = useRef(false);

    const grid = useRef();
    const chart = useRef();
    const actionBar = useRef();
    const autoRefreshTimer = useRef();
    const autoResetUITimer = useRef();
    const filteredGroup = useRef();

    const searchParentConditions =
        props.FKField && props.parentData && typeof props.parentData === 'object'
            ? {
                  [props.FKField]: props.parentData[props.parentIDField ? props.parentIDField : 'code']
              }
            : {};

    const searchConditions = useMemo(() => {
        return {};
    }, []);

    const searchText = useRef(defaultStates.searchText);
    const [searchBarFocused, setSearchBarFocused] = useState(false);

    const selectedCategories = [
        selectedCats1.current,
        selectedCats2.current,
        selectedCats3.current,
        selectedCats4.current,
        selectedCats5.current,
        selectedCats6.current,
        selectedCats7.current,
        selectedCats8.current,
        selectedCats9.current,
        selectedCats10.current
    ]
        .flat()
        .map((s) => s.value)
        .flat();

    const totalCats =
        itemsCats1.current.length +
        itemsCats2.current.length +
        itemsCats3.current.length +
        itemsCats4.current.length +
        itemsCats5.current.length +
        itemsCats6.current.length +
        itemsCats7.current.length +
        itemsCats8.current.length +
        itemsCats9.current.length +
        itemsCats10.current.length;

    const dataSource = useRef(initDataSource());

    const pivotGridDataSource = useRef(initPivotDataSource(dataSource.current));
    // const pivotGridDataSource = initPivotDataSource(dataSource);

    const mainActions = useMemo(() => initActions(), []);

    const [showSearchForm, setShowSearchForm] = useState(
        defaultStates && defaultStates.showSearchForm !== undefined ? Boolean(defaultStates.showSearchForm) : false
    );

    const [showChart, setShowChart] = useState(defaultStates && defaultStates.showChart !== undefined ? Boolean(defaultStates.showChart) : false);

    const [formSearchConditions, setFormSearchConditions] = useState({ ...props.searchConditions });

    if (!filteredGroup.current)
        filteredGroup.current = defaultStates.filteredGroup ? JSON.parse(defaultStates.filteredGroup) : initDefaultFilteredGroup();

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const [shouldRender, setShouldRender] = useState(1);

    useImperativeHandle(ref, () => ({
        // forceReload: forceReload,
        resetSelectedActionGroups: resetSelectedActionGroups,
        refresh: refresh,
        getKeyField: getKeyField,
        forceRender: forceRender,
        isDataLoaded: isDataLoaded,
        isDataLoading: isDataLoading,
        getSearchCategories: getSearchCategories
    }));

    useEffect(() => {
        autoBindChart();
        // forceReload();
    }, []);

    useEffect(() => {
        saveCachingStates();
    }, [showChart, showSearchForm]);

    // useEffect(() => {
    //     autoReload();
    // }, [searchText.current]);

    useEffect(() => {
        generateSearchConditions({ autoReload: true });
    }, [props.searchConditions, props.actionItems, props.searchFields, formSearchConditions]);

    const isCategoriesShouldVisible = (cats) => {
        if (!cats) return false;

        let isVisible = true;
        if (!isVisible) return false;

        let ar1 = [
            props.searchCategories1,
            props.searchCategories2,
            props.searchCategories3,
            props.searchCategories4,
            props.searchCategories5,
            props.searchCategories6,
            props.searchCategories7,
            props.searchCategories8,
            props.searchCategories9,
            props.searchCategories10
        ];
        let ar2 = [
            selectedCats1.current.filter((s) => s.value && s.value.length > 0),
            selectedCats2.current.filter((s) => s.value && s.value.length > 0),
            selectedCats3.current.filter((s) => s.value && s.value.length > 0),
            selectedCats4.current.filter((s) => s.value && s.value.length > 0),
            selectedCats5.current.filter((s) => s.value && s.value.length > 0),
            selectedCats6.current.filter((s) => s.value && s.value.length > 0),
            selectedCats7.current.filter((s) => s.value && s.value.length > 0),
            selectedCats8.current.filter((s) => s.value && s.value.length > 0),
            selectedCats9.current.filter((s) => s.value && s.value.length > 0),
            selectedCats10.current.filter((s) => s.value && s.value.length > 0)
        ];
        let ar3 = [
            itemsCats1.current,
            itemsCats2.current,
            itemsCats3.current,
            itemsCats4.current,
            itemsCats5.current,
            itemsCats6.current,
            itemsCats7.current,
            itemsCats8.current,
            itemsCats9.current,
            itemsCats10.current
        ];

        let currentCatIndex = ar1.findIndex((a) => stringify(a) === stringify(cats));
        let currentCatTotals = ar3[currentCatIndex];
        let currentCatSelecteds = ar2[currentCatIndex];

        if (cats.visibleDependOthers) {
            if (totalCats > currentCatTotals.length) {
                if (cats.visibleDependOthers instanceof Array) {
                    if (cats.visibleDependOthersType !== 'OR')
                        isVisible = cats.visibleDependOthers.every((oName) => {
                            for (let i = 0; i < ar1.length; i++) {
                                const otherCat = ar1[i];
                                const otherCatSelecteds = ar2[i];
                                const otherCatTotals = ar3[i];
                                if (otherCat && stringify(otherCat) !== stringify(cats)) {
                                    if (
                                        otherCat &&
                                        otherCat.lookups &&
                                        otherCat.lookups.some((l) => l.name === oName) &&
                                        (otherCatTotals.length <= 0 || otherCatSelecteds.length > 0)
                                    )
                                        return true;
                                }
                            }

                            return false;
                        });
                    else {
                        isVisible = cats.visibleDependOthers.some((oName) => {
                            for (let i = 0; i < ar1.length; i++) {
                                const otherCat = ar1[i];
                                const otherCatSelecteds = ar2[i];
                                const otherCatTotals = ar3[i];
                                if (otherCat && stringify(otherCat) !== stringify(cats)) {
                                    if (
                                        otherCat &&
                                        otherCat.lookups &&
                                        otherCat.lookups.some((l) => l.name === oName) &&
                                        otherCatSelecteds.length > 0
                                    )
                                        return true;
                                }
                            }

                            return false;
                        });

                        if (!isVisible)
                            isVisible = cats.visibleDependOthers.every((oName) => {
                                for (let i = 0; i < ar1.length; i++) {
                                    const otherCat = ar1[i];
                                    const otherCatSelecteds = ar2[i];
                                    const otherCatTotals = ar3[i];
                                    if (otherCat && stringify(otherCat) !== stringify(cats)) {
                                        if (
                                            otherCat &&
                                            otherCat.lookups &&
                                            otherCat.lookups.some((l) => l.name === oName) &&
                                            otherCatTotals.length > 0
                                        )
                                            return false;
                                    }
                                }

                                return true;
                            });
                    }
                } else {
                    isVisible = selectedCategories.length > 0;
                }
            }
        } else {
            isVisible = currentCatTotals.length > 1 || currentCatSelecteds.length > 0;
        }
        return isVisible;
    };

    let cats1Visible = isCategoriesShouldVisible(props.searchCategories1);
    let cats2Visible = isCategoriesShouldVisible(props.searchCategories2);
    let cats3Visible = isCategoriesShouldVisible(props.searchCategories3);
    let cats4Visible = isCategoriesShouldVisible(props.searchCategories4);
    let cats5Visible = isCategoriesShouldVisible(props.searchCategories5);
    let cats6Visible = isCategoriesShouldVisible(props.searchCategories6);
    let cats7Visible = isCategoriesShouldVisible(props.searchCategories7);
    let cats8Visible = isCategoriesShouldVisible(props.searchCategories8);
    let cats9Visible = isCategoriesShouldVisible(props.searchCategories9);
    let cats10Visible = isCategoriesShouldVisible(props.searchCategories10);

    return (
        <React.Fragment>
            {shouldRender && Boolean(props.search) && (
                <div style={{ display: showSearchForm ? 'block' : 'none' }}>
                    <p />
                    <AntSearchForm
                        formData={formSearchConditions}
                        shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                        prepareConditions={props.search ? props.search.prepareConditions : undefined}
                        default={props.search ? props.search.default : undefined}
                        API={props.search && props.search.API ? props.search.API : props.API}
                        table={props.search && props.search.table ? props.search.table : props.table}
                        entityName={props.API.entityName ? props.API.entityName : props.entityName}
                        entityDisplay={props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay}
                        colCount={props.search && props.search.colCount !== undefined ? props.search.colCount : undefined}
                        colCountDefault={props.search ? props.search.colCountDefault : undefined}
                        // onConditionsChanged={setFormSearchConditions}
                        onFormDataChanged={setFormSearchConditions}
                        fields={props.fields}
                        items={props.search && props.search.items instanceof Array ? props.search.items : []}
                    />
                    <p />
                </div>
            )}

            {shouldRender && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {props.searchCategories1 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories1}
                            selectedItems={selectedCats1.current}
                            // autoSelectFirst={
                            //     props.autoSelectFirstCategory &&
                            //     selectedCats1.current.length + selectedCats2.current.length + selectedCats3.current.length + selectedCats4.current.length + selectedCats5.current.length <= 0
                            // }
                            onLoadedData={(e) => {
                                itemsCats1.current = e.allItems;
                                itemsCats1Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats1.current = e.selectedItems;
                                itemsCats1.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats1Visible}
                            allowQuery={() => {
                                if (props.searchCategories1.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}

                    {props.searchCategories2 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories2}
                            selectedItems={selectedCats2.current}
                            onLoadedData={(e) => {
                                itemsCats2.current = e.allItems;
                                itemsCats2Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats2.current = e.selectedItems;
                                itemsCats2.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats2Visible}
                            allowQuery={() => {
                                if (props.searchCategories2.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}

                    {props.searchCategories3 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories3}
                            selectedItems={selectedCats3.current}
                            onLoadedData={(e) => {
                                itemsCats3.current = e.allItems;
                                itemsCats3Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats3.current = e.selectedItems;
                                itemsCats3.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats3Visible}
                            allowQuery={() => {
                                if (props.searchCategories3.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}

                    {props.searchCategories4 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories4}
                            selectedItems={selectedCats4.current}
                            onLoadedData={(e) => {
                                itemsCats4.current = e.allItems;
                                itemsCats4Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats4.current = e.selectedItems;
                                itemsCats4.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats4Visible}
                            allowQuery={() => {
                                if (props.searchCategories4.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}

                    {props.searchCategories5 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories5}
                            selectedItems={selectedCats5.current}
                            onLoadedData={(e) => {
                                itemsCats5.current = e.allItems;
                                itemsCats5Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats5.current = e.selectedItems;
                                itemsCats5.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats5Visible}
                            allowQuery={() => {
                                if (props.searchCategories5.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}

                    {props.searchCategories6 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories6}
                            selectedItems={selectedCats6.current}
                            onLoadedData={(e) => {
                                itemsCats6.current = e.allItems;
                                itemsCats6Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats6.current = e.selectedItems;
                                itemsCats6.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats6Visible}
                            allowQuery={() => {
                                if (props.searchCategories6.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}

                    {props.searchCategories7 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories7}
                            selectedItems={selectedCats7.current}
                            onLoadedData={(e) => {
                                itemsCats7.current = e.allItems;
                                itemsCats7Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats7.current = e.selectedItems;
                                itemsCats7.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats7Visible}
                            allowQuery={() => {
                                if (props.searchCategories7.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}

                    {props.searchCategories8 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories8}
                            selectedItems={selectedCats8.current}
                            onLoadedData={(e) => {
                                itemsCats8.current = e.allItems;
                                itemsCats8Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats8.current = e.selectedItems;
                                itemsCats8.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats8Visible}
                            allowQuery={() => {
                                if (props.searchCategories8.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}

                    {props.searchCategories9 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories9}
                            selectedItems={selectedCats9.current}
                            onLoadedData={(e) => {
                                itemsCats9.current = e.allItems;
                                itemsCats9Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats9.current = e.selectedItems;
                                itemsCats9.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats9Visible}
                            allowQuery={() => {
                                if (props.searchCategories9.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}

                    {props.searchCategories10 && (
                        <AntGridCategories
                            gridSettingsKey={pivotSettingsKey}
                            gridSettingsMeta={pivotSettingsMeta}
                            API={props.API}
                            searchText={searchText.current}
                            searchConditions={searchConditions.current}
                            searchCategories={props.searchCategories10}
                            selectedItems={selectedCats10.current}
                            onLoadedData={(e) => {
                                itemsCats10.current = e.allItems;
                                itemsCats10Loaded.current = true;
                            }}
                            onSelectionChanged={(e) => {
                                selectedCats10.current = e.selectedItems;
                                itemsCats10.current = e.allItems;
                                generateSearchConditions({ autoReload: true });
                                forceRender();
                            }}
                            visible={cats10Visible}
                            allowQuery={() => {
                                if (props.searchCategories10.visibleDependOthers) return selectedCategories.length > 0;
                                return true;
                            }}
                        ></AntGridCategories>
                    )}
                </div>
            )}

            {shouldRender && props.showActionBar != false && (
                <AntGridBar
                    ref={actionBar}
                    shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                    dataSource={dataSource.current}
                    showRefresh={showRefresh}
                    showChart={Boolean(props.chart) || props.showChart}
                    showFilterBuilder={props.showFilterBuilder}
                    showFilter={Boolean(props.search)}
                    onFilterClick={() => setShowSearchForm((v) => !Boolean(v))}
                    onShowChart={() => setShowChart((v) => !Boolean(v))}
                    showDownload={props.showDownload}
                    showSearchBar={props.showSearchBar}
                    searchBarWidth={props.searchBarWidth}
                    showFieldChooser={props.showFieldChooser}
                    locationRefresh={props.locationRefresh ? props.locationRefresh : 'before'}
                    locationDownload={props.locationDownload ? props.locationDownload : 'after'}
                    locationSearchBar={props.locationSearchBar ? props.locationSearchBar : 'after'}
                    searchText={searchText.current}
                    autoFocusSearchBar={searchBarFocused ? false : props.autoFocusSearchBar}
                    onRefreshClick={reload}
                    onSearchText={onSearchText}
                    onStartSearchText={onStartSearchText}
                    // onEndSearchText={onEndSearchText}
                    onAction={onAction}
                    onFilteredByGroup={(gr) => {
                        filteredGroup.current = gr;
                        generateSearchConditions({ autoReload: true });
                    }}
                    onDownloadClick={onActionDownload}
                    items={mainActions}
                    filteredGroup={filteredGroup.current ? filteredGroup.current.name : null}
                />
            )}

            {shouldRender && !props.hideHeaderMargin && props.showActionBar === false && <p />}

            {shouldRender && (Boolean(props.chart) || props.showChart) && (
                <div style={{ display: showChart ? 'block' : 'none' }}>
                    <Chart ref={chart}>
                        <Margin top={30} bottom={10} left={10} right={10} />
                        <Size height={props.chart && props.chart.height ? props.chart.height : 230} />
                        {/* <Tooltip enabled={true} /> */}
                        <CommonSeriesSettings type="bar" barWidth={25} aggregation={{ enabled: true, method: 'sum' }} />
                        <AdaptiveLayout width={450} />
                    </Chart>
                </div>
            )}

            {shouldRender && (
                <PivotGrid
                    ref={grid}
                    shouldRender={shouldRender + (props.shouldRender ? props.shouldRender : 1)}
                    className={'dx-card wide-card ant-grid ant-pivotgrid'}
                    dataSource={pivotGridDataSource.current}
                    loadPanel={{ enabled: false }}
                    // height={props.height !== undefined ? props.height : 'auto'}
                    // onOptionChanged={this.onOptionChanged}
                    wordWrapEnabled={false}
                    allowSortingBySummary={false}
                    allowFiltering={false}
                    allowSorting={true}
                    showBorders={true}
                    showColumnTotals={props.showColumnTotals !== undefined ? props.showColumnTotals : false}
                    showColumnGrandTotals={props.showColumnGrandTotals !== undefined ? props.showColumnGrandTotals : false}
                    showRowTotals={props.showRowTotals !== undefined ? props.showRowTotals : false}
                    showRowGrandTotals={props.showRowGrandTotals !== undefined ? props.showRowGrandTotals : false}
                    // onInitialized={(e) => {
                    //     e.component.columnOption('command:adaptive', 'visible', false);
                    // }}
                    // onContentReady={(e) => {
                    //     e.component.updateDimensions();
                    // }}
                    onCellPrepared={props.onCellPrepared}
                    {...props.options}
                    visible={props.visible !== undefined ? props.visible : true}
                >
                    <FieldChooser enabled={false} />
                </PivotGrid>
            )}
        </React.Fragment>
    );
});
